import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { fetchDictionaries, subscribeToDictionaries } from '../../../services/DictionaryService';
import { BUNDLED_TRANSLATIONS } from '../../../utils/helpers/defaultLanguage';
import { isSupportedLocale, readPreferredLocale, savePreferredLocale } from '../../../utils/helpers/localePreference';
import { MISSING_TRANSLATION, interpolate } from '../../../utils/helpers/translation';

export const DICTIONARY_STATUS = {
  LOADING: 'loading',
  READY: 'ready',
  ERROR: 'error'
};

const LocalizationContext = createContext();

/**
 * @param {Object} bundled - Dictionaries shipped with the app, keyed by language code.
 * @param {Object|null} backend - Dictionaries from the backend, keyed by language code.
 * @returns {Object} The backend dictionaries layered over the bundled ones.
 * @author armandoricosandoval -ai-
 */
const mergeDictionaries = (bundled, backend) => ({
  en: { ...bundled.en, ...backend?.en },
  es: { ...bundled.es, ...backend?.es }
});

/**
 * Owns the locale and the dictionaries of the whole app.
 * @param {Object} props - Component props.
 * @param {React.ReactNode} props.children - Subtree that gets access to the translator.
 * @param {Object} [props.translations] - Dictionaries replacing the bundled JSON.
 * @returns {JSX.Element} The provider wrapping its children.
 * @author armandoricosandoval -ai-
 */
export const LocalizationProvider = ({ children, translations: bundledTranslations = BUNDLED_TRANSLATIONS }) => {
  const [locale, setLocaleState] = useState(readPreferredLocale);
  const [backendDictionaries, setBackendDictionaries] = useState(null);
  const [status, setStatus] = useState(DICTIONARY_STATUS.LOADING);

  useEffect(() => {
    let isMounted = true;
    const applyDictionaries = (dictionaries) => {
      if (isMounted) {
        setBackendDictionaries(dictionaries);
        setStatus(DICTIONARY_STATUS.READY);
      }
    };
    const unsubscribe = subscribeToDictionaries(applyDictionaries);
    fetchDictionaries()
      .then(applyDictionaries)
      .catch(() => isMounted && setStatus(DICTIONARY_STATUS.ERROR));
    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  useEffect(() => {
    document.documentElement.lang = locale;
  }, [locale]);

  const dictionaries = useMemo(
    () => mergeDictionaries(bundledTranslations, backendDictionaries),
    [bundledTranslations, backendDictionaries]
  );

  const setLocale = useCallback((nextLocale) => {
    if (isSupportedLocale(nextLocale)) {
      savePreferredLocale(nextLocale);
      setLocaleState(nextLocale);
    }
  }, []);

  const t = useCallback(
    (key, data, defaultMessage) => {
      const value = dictionaries[locale]?.[key];
      return value ? interpolate(value, data) : defaultMessage || MISSING_TRANSLATION;
    },
    [dictionaries, locale]
  );

  const value = useMemo(() => ({ t, locale, setLocale, status, dictionaries }), [t, locale, setLocale, status, dictionaries]);

  return <LocalizationContext.Provider value={value}>{children}</LocalizationContext.Provider>;
};

export const useLocalization = () => useContext(LocalizationContext);
